/**
 * Admin Dashboard Page
 * Provides administrative interface for system monitoring and configuration
 * Access restricted to admin users only via ProtectedRoute
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import UsageAnalytics from '../components/UsageAnalytics';
import UserManagement from '../components/UserManagement';
import ModelManagement from '../components/ModelManagement';
import AuditLogViewer from '../components/AuditLogViewer';
import SystemDetails from '../components/SystemDetails';

type ActiveTab = 'overview' | 'analytics' | 'users' | 'models' | 'audit' | 'system';

interface QuickStats {
  activeUsers: number;
  totalConversations: number;
  messagesToday: number;
}

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [stats, setStats] = useState<QuickStats>({ activeUsers: 0, totalConversations: 0, messagesToday: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/admin/dashboard', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          const d = data.dashboard || data;
          setStats({
            activeUsers: d.users?.active ?? 0,
            totalConversations: d.conversations?.total ?? 0,
            messagesToday: d.usage?.last30Days?.totalMessages ?? 0,
          });
        }
      } catch {
        // Stats are non-critical
      }
    };
    fetchStats();
  }, []);

  const tabs: { id: ActiveTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'users', label: 'Users' },
    { id: 'models', label: 'Models' },
    { id: 'audit', label: 'Audit Logs' },
    { id: 'system', label: 'System Details' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Admin Dashboard
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Welcome back, {user?.name}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="/"
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Back to Chat
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {/* Active Users Card */}
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveTab('users')}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Active Users
                </h2>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.activeUsers}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Users active in the last 7 days
              </p>
            </div>

            {/* Usage Analytics Card */}
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveTab('analytics')}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Usage Analytics
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                View usage trends and cost breakdowns
              </p>
              <div className="mt-4">
                <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  View Analytics &rarr;
                </span>
              </div>
            </div>

            {/* User Management Card */}
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveTab('users')}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  User Management
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage users and configure rate limits
              </p>
              <div className="mt-4">
                <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  View Users &rarr;
                </span>
              </div>
            </div>

            {/* Model Management Card */}
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveTab('models')}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
                  <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Model Management
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure models and API keys
              </p>
              <div className="mt-4">
                <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  View Models &rarr;
                </span>
              </div>
            </div>

            {/* Audit Logs Card */}
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveTab('audit')}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Audit Logs
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Review security events and system activity
              </p>
              <div className="mt-4">
                <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  View Audit Logs &rarr;
                </span>
              </div>
            </div>

            {/* Quick Stats Card */}
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setActiveTab('system')}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/40 rounded-lg">
                  <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Quick Stats
                </h2>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Conversations</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.totalConversations || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Messages Today</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.messagesToday || '-'}</span>
                </div>
              </div>
              <div className="mt-3">
                <span className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  System Details &rarr;
                </span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && <UsageAnalytics />}

        {activeTab === 'users' && <UserManagement />}

        {activeTab === 'models' && <ModelManagement />}

        {activeTab === 'audit' && <AuditLogViewer />}

        {activeTab === 'system' && <SystemDetails />}
      </main>
    </div>
  );
};

export default AdminPage;
